import bus from 'common/bus'
import { oneApi } from 'config/api'
import dataReport from 'common/dataReport'
import overlayDetector from './overlayDetector'
import componentPriority from './componentPriority'

// 模块级标记：当前页访问是否已展示过任意弹窗（多个弹窗实例共用，避免一页弹多个）
let visitShown = false

export default {
  // 由 CMS（B 端 PopupComponent.vue）配置后下发的字段，对应弹窗渲染和触发所需的所有配置项
  props: {
    componentName: { default: '', type: String },
    componentType: { default: 0, type: Number },
    componentTitle: { default: '', type: String },
    moduleId: { default: 0, type: Number },
    locale: { default: '', type: String },
    registration: { default: () => ({}), type: Object },
    title: { default: '', type: String },
    content: { default: '', type: String },
    webUrl: { default: '', type: String },
    webAltInfo: { default: '', type: String },
    touchUrl: { default: '', type: String },
    touchAltInfo: { default: '', type: String },
    triggerType: { default: 'delay', type: String },
    delaySeconds: { default: 0, type: Number },
    scrollPercent: { default: 50, type: Number },
    inputPlaceholder: { default: '', type: String },
    buttonText: { default: '', type: String },
    showCheckbox: { default: true, type: Boolean },
    complianceText: { default: '', type: String },
    send: { default: '', type: String },
    autoTagIdList: { default: () => [], type: Array },
    clue: { default: '', type: String },
    index: { default: 0, type: Number },
  },
  data() {
    // hk 地区用繁体提示，其余用英文
    const successNotice = this.locale === 'hk'
      ? '提交成功，感謝您對學而思的信任!'
      : 'Thank you for your interest in Think Academy!'
    const privacyWarn = this.locale === 'hk' ? '請同意隱私政策' : 'Please accept the privacy policy'
    return {
      isShow: false,
      scrollHandler: null,
      delayTimer: null,
      emailValue: '',
      checkboxChecked: false,
      loading: false,
      effectiveTrigger: '',
      successNotice,
      privacyWarn,
    }
  },
  computed: {
    // 预览态（带 previewSign query）跳过触发限制和频控，直接展示
    isPreview() {
      return !!this.$route.query.previewSign
    },
    schoolCode() {
      return this.$store.state.schoolCode
    },
    pageId() {
      return this.$store.state.customPage.pageId
    },
    // localStorage 的 key 按「地区 + 页面 + 模块」隔离，避免不同弹窗互相影响
    storageKeyClosed() {
      return `popup-closed-${this.schoolCode}-${this.pageId}-${this.moduleId}`
    },
    storageKeySubmitted() {
      return `popup-submitted-${this.schoolCode}-${this.pageId}-${this.moduleId}`
    },
    isTouch() {
      return process.env.clientType === 'touch'
    },
    // 按端取对应的图片 URL / Alt
    imageUrl() {
      return this.isTouch ? this.touchUrl : this.webUrl
    },
    imageAlt() {
      return this.isTouch ? this.touchAltInfo : this.webAltInfo
    },
    // 即时触发：delay=0 或 scroll=0（mount 即触发）。与悬浮框（mount 即展示）真正同时，走「页面排序优先」
    isSimultaneousTrigger() {
      if (this.effectiveTrigger === 'delay') {
        return Number(this.delaySeconds) === 0
      }
      if (this.effectiveTrigger === 'scroll') {
        return Number(this.scrollPercent) === 0
      }
      return false
    },
  },
  watch: {
    // 切换路由后重置 visitShown，允许新页面重新弹一次
    $route(to, from) {
      if (to.path !== from.path) {
        visitShown = false
      }
    },
  },
  mounted() {
    // 预览态强制立即触发；否则按配置的 triggerType
    this.effectiveTrigger = this.isPreview ? 'immediate' : this.triggerType
    this.setupTrigger()
    // 监听别的弹窗已展示的事件，本实例也跟着标记为已展示
    bus.$on('popup:shown', this.onPopupShown)
  },
  beforeDestroy() {
    if (this.delayTimer) {
      clearTimeout(this.delayTimer)
      this.delayTimer = null
    }
    if (this.scrollHandler && typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }
    // 若弹窗打开时组件被销毁（SPA 路由切换），必须显式关闭让 Vant 递减 lockCount。
    // 同时直接移除 body class 兜底——Vue 2 销毁阶段不保证子组件 watcher 一定触发。
    if (this.isShow) {
      this.isShow = false
      if (typeof document !== 'undefined') {
        document.body.classList.remove('van-overflow-hidden')
      }
    }
    bus.$off('popup:shown', this.onPopupShown)
    componentPriority.unregisterShow(this.index)
  },
  methods: {
    onPopupShown() {
      visitShown = true
    },
    // 按触发类型注册触发器：delay=延时 N 秒，scroll=滚到视口高度的 x% 后弹
    setupTrigger() {
      if (typeof window === 'undefined') return
      if (this.isPreview) {
        this.attemptShow()
        return
      }
      const trigger = this.effectiveTrigger
      if (trigger === 'delay') {
        const sec = Number(this.delaySeconds) || 0
        this.delayTimer = setTimeout(() => {
          this.attemptShow()
        }, sec * 1000)
      } else if (trigger === 'scroll') {
        let ticking = false
        this.scrollHandler = () => {
          if (ticking) return
          ticking = true
          window.requestAnimationFrame(() => {
            ticking = false
            const vh = window.innerHeight || 1
            // 进度按可视窗口高度算（scrollY / vh），百分比可大于 100
            const progress = window.scrollY / vh
            // 兜底：页面不够长、滚不到配置百分比时，触底也触发
            // 两个前置条件避免误触发：
            //  - scrollY > 0：mount 时 scrollY=0 不算"用户滚到底"，不触发
            //  - docHeight > vh：要求页面真的比视口高（可滚动），异步加载时 docHeight 暂时偏小不算
            const docHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0
            const reachedBottom = window.scrollY > 0
              && docHeight > vh
              && window.scrollY + vh >= docHeight - 2
            if (progress * 100 >= (Number(this.scrollPercent) || 0) || reachedBottom) {
              this.attemptShow()
              if (this.scrollHandler) {
                window.removeEventListener('scroll', this.scrollHandler)
                this.scrollHandler = null
              }
            }
          })
        }
        window.addEventListener('scroll', this.scrollHandler, { passive: true })
        this.scrollHandler()
      }
    },
    // 真正决定是否展示的关口：已展示过 / 已提交 / 24h 内关闭过 / 有其他浮层在弹 都跳过
    attemptShow() {
      if (visitShown) return
      if (!this.isPreview) {
        if (this.isSubmitted()) return
        if (this.isClosed24h()) return
        if (overlayDetector.isOverlayActive()) return
        // 与营销悬浮框的冲突处理（混合方案）：
        //  - 即时触发：按页面排序，靠前悬浮框在展示则让行（hasEarlierShowing）
        //  - delay>0：业务互斥，任意悬浮窗在展示就不弹（hasAnyShowing）
        if (this.isSimultaneousTrigger) {
          if (componentPriority.hasEarlierShowing(this.index)) return
        } else if (componentPriority.hasAnyShowing()) {
          return
        }
      }
      this.isShow = true
      visitShown = true
      bus.$emit('popup:shown', this.moduleId)
      // 仅即时触发时登记，广播给靠后的悬浮框让其隐藏；delay>0 不登记（悬浮窗不因延迟弹窗隐藏）
      if (this.isSimultaneousTrigger) {
        componentPriority.registerShow(this.index, 'popup')
      }
    },
    isSubmitted() {
      if (typeof localStorage === 'undefined') return false
      return !!localStorage.getItem(this.storageKeySubmitted)
    },
    isClosed24h() {
      if (typeof localStorage === 'undefined') return false
      const ts = Number(localStorage.getItem(this.storageKeyClosed) || 0)
      if (!ts) return false
      return Date.now() - ts < 24 * 60 * 60 * 1000
    },
    markClosed() {
      if (typeof localStorage === 'undefined') return
      localStorage.setItem(this.storageKeyClosed, String(Date.now()))
    },
    markSubmitted() {
      if (typeof localStorage === 'undefined') return
      localStorage.setItem(this.storageKeySubmitted, '1')
    },
    // 关闭弹窗：隐藏 + 记 24h 频控时间戳
    closePopup() {
      this.isShow = false
      this.markClosed()
      componentPriority.unregisterShow(this.index)
    },
    // 提交线索：组装数据（含邮箱、来源、标签）调 submitLeads 接口；
    // autoTagIdList 优先于 clue 作为标签来源；成功后埋点 + 标记已提交 + 关弹窗
    async save({ successCallback, failCallback } = {}) {
      const data = {
        sourceCode: this.isTouch ? 'touch' : 'website',
        mediaCode: this.isTouch ? 'touch_lead' : 'pc_lead',
        tags: [],
        yachProfile: {},
        formData: [
          {
            key: 'email',
            name: 'Email',
            value: this.emailValue,
            custom: false,
            multi: false,
          },
        ],
        componentId: this.moduleId,
        componentType: 'MODULE',
        componentName: this.componentName,
        emailSubscribe: false,
        moduleType: 35,
      }
      if (this.autoTagIdList && this.autoTagIdList.length > 0) {
        data.tags = this.autoTagIdList.map((item) => item.name)
        data.tagIds = this.autoTagIdList.map((item) => item.id)
      } else if (this.clue !== '') {
        data.tags.push(this.clue)
      }
      this.loading = true
      let res
      try {
        res = await this.$axios.post(oneApi.submitLeads, data, {
          rewritePostBody: true,
          sendToken: !!this.$store.state.ucenter.isLogin,
        })
      } catch (e) {
        this.loading = false
        if (failCallback) failCallback()
        return
      }
      this.loading = false
      if (!res || res.code !== 0) {
        if (failCallback) failCallback(res && res.msg)
        return
      }
      this.trackEvent()
      this.markSubmitted()
      this.isShow = false
      if (successCallback) successCallback()
    },
    // 提交成功埋点：GTM + Facebook + TikTok
    trackEvent() {
      if (window.dataLayer) window.dataLayer.push({ event: 'generatelead_gtm' })
      dataReport.launchReport({
        fbqOptions: { name: 'Lead' },
        ttqOptions: { name: 'SubmitForm' },
      })
    },
    // 点击提交按钮的 GA 埋点（提交动作上报，lego_id 为模块 id）
    GAReport() {
      window?.dataLayer?.push({
        event: 'LP_popup_click',
        lego_id: String(this.moduleId),
      })
    },
  },
}
